import { createSettingMetadataFromGroupDefaults } from "../../settings-catalog/metadata";
import type {
  SettingDefinition,
  SettingMetadata,
  SettingRegistryMetadataInput,
  TeamSettingsSettingValueSource,
} from "../../settings-catalog/types";
import {
  WarrantyRegistrationRewardsFlatTeamSettingGroupById,
  type WarrantyRegistrationRewardsFlatTeamSettingGroupId,
} from "./groups";

type WarrantyRegistrationRewardsSettingInput = SettingRegistryMetadataInput;

function setting(
  metadata: WarrantyRegistrationRewardsSettingInput,
): WarrantyRegistrationRewardsSettingInput {
  return metadata;
}

function warrantyRegistrationRewardsGroup<
  const Group extends WarrantyRegistrationRewardsFlatTeamSettingGroupId,
  const T extends Record<string, WarrantyRegistrationRewardsSettingInput>,
>(group: Group, settings: T): { [K in keyof T]: T[K] & { group: Group } } {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return Object.fromEntries(
    Object.entries(settings).map(([key, value]) => [key, { ...value, group }]),
  ) as { [K in keyof T]: T[K] & { group: Group } };
}

// Keys must be real flat schema keys from ./settings.ts (wired into
// FlatTeamSettingsSchema). Each equals the team.settings.* path, dots→slashes.
const WarrantyRegistrationRewardsRegistry = {
  ...warrantyRegistrationRewardsGroup(
    "warranty_registration_rewards.discount",
    {
      "warranties/registrationRewardDiscountId": setting({
        label: "Registration reward discount",
        description:
          "Discount offered as a reward to customers who register products for warranty coverage.",
        synonyms: [
          "registration reward",
          "registration reward discount",
          "warranty registration discount",
          "registration discount",
        ],
      }),
    },
  ),
};

type WarrantyRegistrationRewardsFlatTeamSettingKey =
  keyof typeof WarrantyRegistrationRewardsRegistry;
// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
const WarrantyRegistrationRewardsFlatTeamSettingKeys = Object.keys(
  WarrantyRegistrationRewardsRegistry,
) as WarrantyRegistrationRewardsFlatTeamSettingKey[];

export const WarrantyRegistrationRewardsFlatTeamSettingCatalogDefinitions =
  WarrantyRegistrationRewardsFlatTeamSettingKeys.map((key) => {
    const definition = WarrantyRegistrationRewardsRegistry[key];
    return {
      id: key,
      groupId: definition.group,
      metadata: createSettingMetadataFromGroupDefaults({
        setting: definition,
        group:
          WarrantyRegistrationRewardsFlatTeamSettingGroupById[definition.group],
      }),
      valueSource: { adapter: "teamSettings", key },
    };
  }) satisfies readonly SettingDefinition<
    WarrantyRegistrationRewardsFlatTeamSettingKey,
    WarrantyRegistrationRewardsFlatTeamSettingGroupId,
    TeamSettingsSettingValueSource<WarrantyRegistrationRewardsFlatTeamSettingKey>
  >[];

export const WarrantyRegistrationRewardsFlatTeamSettingMetadataByKey =
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  Object.fromEntries(
    WarrantyRegistrationRewardsFlatTeamSettingCatalogDefinitions.map(
      (definition) => [definition.id, definition.metadata],
    ),
  ) as Record<WarrantyRegistrationRewardsFlatTeamSettingKey, SettingMetadata>;
